import { useContext } from 'react'
import { AuthContext, type LocalAuthApi, type SsoAuthApi } from './AuthContext'

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * The api for a page that owns credentials: sign-in, sign-up, password reset.
 *
 * Mounting such a page in sso mode is a routing bug, not a state to render --
 * every control on it would be dead -- so this fails loudly instead. A double
 * with no mode at all is local, which is what a hand-built test literal is.
 */
export function useLocalAuth(): LocalAuthApi {
  const api = useAuth()
  if (api.authMode === 'sso') {
    throw new Error(
      'useLocalAuth() was called in sso mode: this page has no credential entry points to offer.',
    )
  }
  return api as LocalAuthApi
}

/** The api for the sso redirect and callback pages. Mounting one of those in
 *  local mode is the same kind of routing bug, and fails the same way. */
export function useSsoAuth(): SsoAuthApi {
  const api = useAuth()
  if (!api.startSsoLogin) {
    throw new Error('useSsoAuth() requires an AuthProvider in sso mode.')
  }
  return api as SsoAuthApi
}
